import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  TrendingUp, 
  Share2, 
  Users, 
  Rocket 
} from 'lucide-react';

const Objetivos = () => {
  const objectives = [
    {
      icon: <Target size={32} />,
      text: "Reflexionar sobre los 50 años de experiencia de la UTPL en educación a distancia y en línea."
    },
    {
      icon: <TrendingUp size={32} />,
      text: "Analizar los retos actuales y futuros de la educación en línea."
    },
    {
      icon: <Share2 size={32} />,
      text: "Compartir experiencias y buenas prácticas en educación en línea y educación abierta."
    },
    {
      icon: <Users size={32} />,
      text: "Fortalecer la comunidad universitaria a través del encuentro entre docentes, estudiantes y alumni, compartiendo experiencia de éxito logradas por Alumni UTPL."
    },
    {
      icon: <Rocket size={32} />,
      text: "Fomentar la participación estudiantil en la identificación y resolución de retos vinculados a la educación en línea, mediante el desarrollo de propuestas innovadoras y colaborativas."
    }
  ];

  // Variantes para la animación de entrada en cascada
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <section className="min-h-screen py-24 bg-[#f8fafc] relative overflow-hidden flex items-center font-sans">
      {/* Decoración de fondo */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#004270]/5 rounded-full translate-x-1/3 -translate-y-1/3 blur-[120px]"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#febe10]/10 rounded-full -translate-x-1/2 translate-y-1/2 blur-[100px]"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
        {/* Encabezado de la sección */}
        <div className="text-center mb-20 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1 rounded-full bg-[#004270]/10 border border-[#004270]/20 text-[#004270] text-[10px] font-black uppercase tracking-[0.3em] mb-2"
          >
            Propósito del Congreso
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-extrabold text-[#004270] tracking-tight"
          >
            Nuestros <span className="text-[#febe10]">Objetivos</span>
          </motion.h2>
          
          <motion.div 
            initial={{ width: 0 }}
            whileInView={{ width: "60px" }}
            viewport={{ once: true }}
            className="h-1.5 bg-[#febe10] mx-auto rounded-full mt-4"
          ></motion.div>
        </div>

        {/* Grid de Objetivos */}
        <motion.div 
          className="flex flex-wrap justify-center items-stretch  gap-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {objectives.map((obj, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ 
                y: -12,
                transition: { duration: 0.3 }
              }}
              className={`
                basis-full sm:basis-1/2 lg:basis-[30%] 
                relative group overflow-hidden
                bg-[#004270] p-8 md:p-10 rounded-[2.5rem] 
                border border-[#004270]/10 shadow-2xl shadow-[#004270]/20
                transition-all duration-500 hover:shadow-[#004270]/40
                ${index === 3 || index === 4 ? 'lg:col-span-1.5' : ''}
              `}
            >
              {/* Círculo decorativo interno */}
              <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/5 rounded-full group-hover:scale-150 transition-transform duration-700"></div>

              {/* Contenedor del Icono */}
              <div className="relative w-14 h-14 rounded-2xl bg-[#febe10] flex items-center justify-center text-[#004270] mb-8 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg shadow-black/20">
                {obj.icon}
              </div>

              {/* Texto del Objetivo */}
              <div className="relative">
                <p className="text-lg text-white/95 font-medium leading-relaxed transition-colors duration-500 group-hover:text-white">
                  {obj.text}
                </p>
                
                {/* Indicador inferior */}
                <div className="mt-8 flex items-center gap-3">
                    <div className="w-10 h-1 bg-[#febe10] rounded-full"></div>
                    <div className="w-2 h-1 bg-[#febe10]/40 rounded-full group-hover:w-4 transition-all duration-500"></div>
                </div>
              </div>

              {/* Overlay de brillo al hacer hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Objetivos;